import { AlertTriangle, CalendarClock, MailCheck, Trash2 } from 'lucide-react';

import React, { useState } from 'react';

import { formatDate } from '@splitfiesta/core';
import { useNamespace, useTranslation } from '@splitfiesta/i18n';
import { formatSignedCurrencyWithCode } from '@splitfiesta/shared';
import { Alert, Button, Card, FormButton } from '@splitfiesta/ui';

import { ConfirmationDialog } from '@/components/common/ConfirmationDialog';
import { deriveBalanceLines } from '@/components/dashboard/GlobalBalanceCard';

import { useUserNetBalanceQuery } from '@/hooks/queries/useBalances';
import {
  useCancelAccountDeletionMutation,
  useRequestAccountDeletionMutation,
} from '@/hooks/queries/useUser';

import { useAuthStore } from '@/stores/authStore';

import { toast } from '@/utils/toast';

/** Consequence bullets — discrete keys, same set as mobile. */
const BULLETS = ['erased', 'comments', 'groups', 'history', 'grace'] as const;

/**
 * Web danger zone (store-compliance item 9, decision D5): the emailed
 * single-use link IS the confirmation — this section only explains the
 * consequences, warns about open balances (advisory, never a gate), and
 * sends the email. While a deletion is scheduled it renders the
 * grace-window banner with the explicit cancel instead; login alone
 * never cancels.
 */
export function AccountDeletionSection() {
  const { t, i18n } = useTranslation();
  // Gate on the bundles: ProfilePage embeds this section but only loads the
  // profile/common namespaces itself — without this, settings: keys flash raw.
  const settingsReady = useNamespace('settings');
  const commonReady = useNamespace('common');

  const user = useAuthStore(state => state.user);
  const netBalance = useUserNetBalanceQuery();
  const requestMutation = useRequestAccountDeletionMutation();
  const cancelMutation = useCancelAccountDeletionMutation();

  const [dialog, setDialog] = useState<'request' | 'cancel' | null>(null);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const email = user?.email ?? '';
  const scheduledAt = user?.deletionEffectiveAt;
  const openBalances = deriveBalanceLines(netBalance.data?.totalsByCurrency);

  if (!settingsReady || !commonReady) {
    return null;
  }

  const submitRequest = async () => {
    setDialog(null);
    setError(null);
    try {
      await requestMutation.mutateAsync();
      setSent(true);
    } catch {
      setError(t('settings:deleteAccount.error'));
    }
  };

  const submitCancel = async () => {
    setDialog(null);
    setError(null);
    try {
      await cancelMutation.mutateAsync();
      // Parity with mobile: confirm the account is staying.
      toast.success(t('settings:deleteAccount.cancelled'));
    } catch {
      setError(t('settings:deleteAccount.error'));
    }
  };

  // Grace window running — banner + explicit cancel only.
  if (scheduledAt) {
    return (
      <Card data-testid="account-deletion-section">
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-500/30 dark:bg-amber-500/10">
          <div className="flex items-center gap-2">
            <CalendarClock className="h-5 w-5 shrink-0 text-amber-600" />
            <h3 className="text-base font-semibold text-amber-800 dark:text-amber-300">
              {t('settings:deleteAccount.scheduledTitle')}
            </h3>
          </div>
          <p className="mt-2 text-sm text-amber-700 dark:text-amber-200/80">
            {t('settings:deleteAccount.scheduledBody', {
              // Local timezone on purpose — mobile renders the same
              // instant locally; the two surfaces must agree on the date.
              date: formatDate(scheduledAt, i18n.language, {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              }),
            })}
          </p>
          {error && <Alert type="error" message={error} className="mt-3" />}
          <Button
            variant="outline"
            className="mt-3 !border-amber-400 !text-amber-700 hover:!bg-amber-100 dark:!text-amber-300 dark:hover:!bg-amber-500/20"
            onClick={() => setDialog('cancel')}
            data-testid="cancel-deletion-button"
          >
            {t('settings:deleteAccount.cancel')}
          </Button>
        </div>
        <ConfirmationDialog
          isOpen={dialog === 'cancel'}
          onClose={() => setDialog(null)}
          onConfirm={() => void submitCancel()}
          title={t('settings:deleteAccount.cancelConfirmTitle')}
          description={t('settings:deleteAccount.cancelConfirmBody')}
          confirmText={t('settings:deleteAccount.cancelConfirmKeep')}
          cancelText={t('common:actions.cancel')}
          isLoading={cancelMutation.isPending}
        />
      </Card>
    );
  }

  // Email sent — nothing more to do on this page until the link is opened.
  if (sent) {
    return (
      <Card data-testid="account-deletion-section">
        <div className="flex flex-col items-center py-4 text-center">
          <MailCheck className="h-8 w-8 text-emerald-600" />
          <h3 className="mt-3 text-lg font-semibold text-gray-900 dark:text-gray-50">
            {t('settings:deleteAccount.sentTitle')}
          </h3>
          <p className="mt-2 max-w-md text-sm text-gray-600 dark:text-gray-300">
            {t('settings:deleteAccount.sentBody', { email })}
          </p>
        </div>
      </Card>
    );
  }

  return (
    <Card data-testid="account-deletion-section">
      <h3 className="mb-1 flex items-center text-lg font-semibold text-error-600 dark:text-error-400">
        <Trash2 className="mr-2 h-5 w-5" />
        {t('settings:dangerZone.title')}
      </h3>
      <p className="text-xs text-gray-500 dark:text-gray-400">
        {t('settings:dangerZone.description')}
      </p>

      <p className="mt-4 text-sm text-gray-700 dark:text-gray-200">
        {t('settings:deleteAccount.intro')}
      </p>
      <ul className="mt-3 list-disc space-y-1 pl-5 text-sm text-gray-600 dark:text-gray-400">
        {BULLETS.map(key => (
          <li key={key}>{t(`settings:deleteAccount.bullets.${key}`)}</li>
        ))}
      </ul>

      {openBalances.length > 0 && (
        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-500/30 dark:bg-amber-500/10">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />
            <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">
              {t('settings:deleteAccount.balancesTitle')}
            </p>
          </div>
          <p className="mt-1 text-sm text-amber-700 dark:text-amber-200/80">
            {t('settings:deleteAccount.balancesBody')}
          </p>
          <div className="mt-2 space-y-0.5">
            {openBalances.map(({ currency, amount }) => (
              <p
                key={currency}
                className="text-sm font-medium text-amber-800 dark:text-amber-300"
              >
                {formatSignedCurrencyWithCode(amount, currency)}
              </p>
            ))}
          </div>
        </div>
      )}

      {error && <Alert type="error" message={error} className="mt-4" />}

      {/* Solid destructive FormButton — the app's delete idiom (matches
          "Delete group" and the FormButton language of ProfilePage). */}
      <FormButton
        type="button"
        variant="error"
        icon={Trash2}
        fullWidth
        className="mt-4"
        onClick={() => setDialog('request')}
        disabled={requestMutation.isPending}
        data-testid="request-deletion-button"
      >
        {t('settings:deleteAccount.send')}
      </FormButton>

      <ConfirmationDialog
        isOpen={dialog === 'request'}
        onClose={() => setDialog(null)}
        onConfirm={() => void submitRequest()}
        title={t('settings:deleteAccount.confirmTitle')}
        description={t('settings:deleteAccount.confirmBody', { email })}
        confirmText={t('settings:deleteAccount.confirmSend')}
        cancelText={t('common:actions.cancel')}
        variant="danger"
        isLoading={requestMutation.isPending}
      />
    </Card>
  );
}
